/**
 * Metadata model versioning: a pure diff between two schema definitions and
 * the value conversions applied when a new version is published. Values are
 * keyed by stable field ids, so relabelling or reordering never touches data.
 * Anything that cannot be converted is preserved in the asset's archive.
 */
import type { FieldDefinition, MetadataSchemaDefinition } from './domain.ts';
import { normalizeDateTime } from './date-schedule.ts';

export type SchemaChangeKind =
  | 'field_added'
  | 'field_removed'
  | 'field_relabelled'
  | 'field_type_changed'
  | 'field_options_removed'
  | 'field_options_added'
  | 'field_required_added'
  | 'field_constraints_changed'
  | 'field_presentation_changed'
  | 'field_permissions_changed'
  | 'sections_changed'
  | 'tabs_changed';

export type ChangeSeverity = 'safe' | 'migrates' | 'archives';

export interface SchemaChange {
  kind: SchemaChangeKind;
  fieldId?: string;
  label: string;
  detail: string;
  severity: ChangeSeverity;
  /** Filled in by the server: assets whose stored values are affected. */
  affectedAssets?: number;
  /** Values that will be moved to the archive instead of converted. */
  archivedValues?: number;
}

/** Structural equality that ignores object key order (JSONB and validators reorder keys). */
function stable(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stable);
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([k, x]) => [k, stable(x)]));
  }
  return v;
}
const same = (a: unknown, b: unknown) => JSON.stringify(stable(a ?? null)) === JSON.stringify(stable(b ?? null));

export function diffSchemas(prev: MetadataSchemaDefinition, next: MetadataSchemaDefinition): SchemaChange[] {
  const changes: SchemaChange[] = [];
  const before = new Map(prev.fields.map((f) => [f.id, f]));
  const after = new Map(next.fields.map((f) => [f.id, f]));
  if (!same(prev.sections, next.sections)) {
    changes.push({ kind: 'sections_changed', label: 'Form sections', detail: 'Sections were added, renamed, reordered or moved to another tab', severity: 'safe' });
  }
  if (!same(prev.tabs, next.tabs)) {
    changes.push({ kind: 'tabs_changed', label: 'Metadata tabs', detail: 'Tabs were added, renamed, reordered or removed', severity: 'safe' });
  }
  for (const f of next.fields) {
    const old = before.get(f.id);
    if (!old) {
      changes.push({ kind: 'field_added', fieldId: f.id, label: f.label, detail: `New ${f.type.replace('_', ' ')} field${f.required ? ' (required for new edits)' : ''}`, severity: 'safe' });
      continue;
    }
    if (old.label !== f.label) changes.push({ kind: 'field_relabelled', fieldId: f.id, label: f.label, detail: `Renamed from “${old.label}”; stored values are unaffected`, severity: 'safe' });
    if (old.type !== f.type) {
      changes.push({ kind: 'field_type_changed', fieldId: f.id, label: f.label, detail: `${old.type} → ${f.type}; convertible values are migrated, the rest archived`, severity: 'migrates' });
    } else if (f.type === 'vocabulary' || f.type === 'tags') {
      const oldValues = new Set((old.options ?? []).map((o) => o.value));
      const newValues = new Set((f.options ?? []).map((o) => o.value));
      const removed = [...oldValues].filter((v) => !newValues.has(v));
      const added = [...newValues].filter((v) => !oldValues.has(v));
      if (removed.length) changes.push({ kind: 'field_options_removed', fieldId: f.id, label: f.label, detail: `Options removed: ${removed.join(', ')}; values using them are archived`, severity: 'archives' });
      if (added.length) changes.push({ kind: 'field_options_added', fieldId: f.id, label: f.label, detail: `Options added: ${added.join(', ')}`, severity: 'safe' });
      if (!removed.length && !added.length && !same(old.options, f.options)) {
        changes.push({ kind: 'field_presentation_changed', fieldId: f.id, label: f.label, detail: 'Option labels or order changed', severity: 'safe' });
      }
    }
    if (!old.required && f.required) changes.push({ kind: 'field_required_added', fieldId: f.id, label: f.label, detail: 'Now required; existing assets without a value are flagged on their next edit', severity: 'safe' });
    if (old.type === f.type && (old.maxLength !== f.maxLength || old.min !== f.min || old.max !== f.max)) {
      changes.push({ kind: 'field_constraints_changed', fieldId: f.id, label: f.label, detail: 'Length or range limits changed; out-of-range values are archived', severity: 'migrates' });
    }
    if (!same(old.viewRoles, f.viewRoles) || !same(old.editRoles, f.editRoles)) {
      changes.push({ kind: 'field_permissions_changed', fieldId: f.id, label: f.label, detail: 'Who can see or edit this field changed', severity: 'safe' });
    }
    if (old.type === f.type && Boolean(old.multiple) !== Boolean(f.multiple)) {
      changes.push({ kind: 'field_constraints_changed', fieldId: f.id, label: f.label, detail: f.multiple ? 'Now allows several values; existing single values are converted to lists' : 'Now allows one value; lists with several values are archived', severity: 'migrates' });
    }
    const presentation: Array<[boolean, string]> = [
      [old.help !== f.help, 'help text'],
      [old.section !== f.section, 'section'],
      [Boolean(old.facet) !== Boolean(f.facet), f.facet ? 'now a search facet' : 'no longer a search facet'],
      [Boolean(old.column) !== Boolean(f.column), f.column ? 'now a table column' : 'no longer a table column'],
      [Boolean(old.hidden) !== Boolean(f.hidden), f.hidden ? 'hidden from forms, facets and search (values are kept)' : 'shown again'],
      [Boolean(old.required) && !f.required, 'no longer required'],
      [Boolean(old.needed) !== Boolean(f.needed), f.needed ? 'now needed (assets without a value get an incomplete-metadata warning)' : 'no longer needed'],
      [Boolean(old.inspector) !== Boolean(f.inspector), f.inspector ? 'now shown in the Library inspector' : 'no longer shown in the Library inspector'],
      [!same(old.visibleWhen, f.visibleWhen), 'conditional visibility'],
      [!same(old.default, f.default), 'default value'],
    ];
    const what = presentation.filter(([changed]) => changed).map(([, label]) => label);
    if (what.length) {
      const text = what.join(', ');
      changes.push({ kind: 'field_presentation_changed', fieldId: f.id, label: f.label, detail: text.charAt(0).toUpperCase() + text.slice(1), severity: 'safe' });
    }
  }
  const oldOrder = prev.fields.map((f) => f.id).filter((id) => after.has(id));
  const newOrder = next.fields.map((f) => f.id).filter((id) => before.has(id));
  if (!same(oldOrder, newOrder)) changes.push({ kind: 'field_presentation_changed', label: 'Field order', detail: 'Fields were reordered', severity: 'safe' });
  for (const f of prev.fields) {
    if (!after.has(f.id)) changes.push({ kind: 'field_removed', fieldId: f.id, label: f.label, detail: 'Removed from the model; existing values are preserved in each asset’s archive', severity: 'archives' });
  }
  return changes;
}

export type Conversion = { ok: true; value: unknown } | { ok: false; reason: string };

/** Convert one stored value from the old field definition to the new one. */
export function convertValue(value: unknown, from: FieldDefinition, to: FieldDefinition): Conversion {
  if (value === undefined || value === null || value === '') return { ok: true, value: undefined };
  const fail = (reason: string): Conversion => ({ ok: false, reason });
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  let v: unknown;
  switch (to.type) {
    case 'text':
    case 'rich_text':
      v = from.type === 'vocabulary' ? from.options?.find((o) => o.value === value)?.label ?? text : text;
      break;
    case 'number': {
      const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
      if (!Number.isFinite(n)) return fail(`“${text}” is not a number`);
      v = n;
      break;
    }
    case 'boolean':
      if (typeof value === 'boolean') v = value;
      else if (/^(true|yes|1)$/i.test(text)) v = true;
      else if (/^(false|no|0)$/i.test(text)) v = false;
      else return fail(`“${text}” is not yes/no`);
      break;
    case 'date':
      // A date-time keeps its (UTC) day.
      v = from.type === 'datetime' && /^\d{4}-\d{2}-\d{2}T/.test(text) ? text.slice(0, 10) : text;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v)) || Number.isNaN(Date.parse(String(v)))) return fail(`“${text}” is not a date`);
      break;
    case 'datetime': {
      // 1.0.0-rc.10: a date becomes midnight UTC of that day; other text must be an ISO 8601 instant.
      const iso = normalizeDateTime(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00Z` : text);
      if (!iso) return fail(`“${text}” is not a date and time`);
      v = iso;
      break;
    }
    case 'timecode':
      if (!/^\d{2}:\d{2}:\d{2}[:;]\d{2}$/.test(text)) return fail(`“${text}” is not a timecode`);
      v = text;
      break;
    case 'tags':
      v = Array.isArray(value) ? value.map(String) : text.split(',').map((t) => t.trim()).filter(Boolean);
      break;
    case 'vocabulary': {
      const options = to.options ?? [];
      const match = (x: string) => options.find((o) => o.value === x || o.label.toLowerCase() === x.toLowerCase())?.value;
      if (to.multiple) {
        const list = (Array.isArray(value) ? value : [value]).map(String);
        const mapped = list.map(match);
        if (mapped.some((m) => !m)) return fail(`“${text}” is not in the list`);
        v = mapped;
      } else {
        const m = match(Array.isArray(value) ? String(value[0]) : text);
        if (!m || (Array.isArray(value) && value.length > 1)) return fail(`“${text}” is not in the list`);
        v = m;
      }
      break;
    }
  }
  if ((to.type === 'text' || to.type === 'rich_text') && to.maxLength && String(v).length > to.maxLength) return fail(`Longer than ${to.maxLength} characters`);
  if (to.type === 'number' && ((to.min !== undefined && (v as number) < to.min) || (to.max !== undefined && (v as number) > to.max))) return fail('Out of range');
  return { ok: true, value: v };
}

/**
 * A new field's id (fixes after rc.9 testing): `f_` and 1–40 lowercase ASCII letters, digits or underscores.
 * Stored models may hold older, longer ids; FieldDefinitionSchema keeps accepting those (`^f_[a-z0-9_]+$`).
 */
export const NEW_FIELD_ID = /^f_[a-z0-9_]{1,40}$/;

/** Letters that do not decompose into a base letter and an accent. */
const FOLD: Record<string, string> = { ß: 'ss', æ: 'ae', ø: 'o', œ: 'oe', đ: 'd', ð: 'd', ł: 'l', þ: 'th', ı: 'i' };

/**
 * The part of a field id after `f_`, from a label: lowercase ASCII (ä → a, ö → o, å → a, é → e …), every run of
 * other characters (spaces included) one underscore, none at the ends, at most 40 characters. Empty when the label
 * has no letters or digits.
 */
export function fieldIdSlug(label: string): string {
  return label.toLowerCase()
    .replace(/[ßæøœđðłþı]/g, (c) => FOLD[c] ?? c)
    .normalize('NFKD').replace(/\p{M}+/gu, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 40).replace(/_+$/, '');
}

/** Why an id cannot be used for a new field, judged by its shape and the draft's own ids; null when it can. */
export function newFieldIdProblem(id: string, draftIds: Iterable<string> = []): string | null {
  if (!id) return 'Give the field an id';
  if (!id.startsWith('f_')) return 'A field id starts with f_';
  if (!NEW_FIELD_ID.test(id)) return id.length > 42 ? 'A field id has at most 40 characters after f_' : 'Use lowercase letters a–z, digits and underscores after f_';
  for (const d of draftIds) if (d === id) return 'Another field in this model already has this id';
  return null;
}

/** Field ids are generated once from the label and never change. */
export function fieldIdFromLabel(label: string, taken: Set<string>): string {
  const base = 'f_' + (fieldIdSlug(label) || 'field');
  let id = base;
  for (let i = 2; taken.has(id); i++) id = `${base}_${i}`;
  return id;
}
